<template>
  <div>
    <fc-form-renderer
      v-if="showRenderer"
      :answers="answers"
      :answers-form-data-format="answersFormData"
      :form="activeForm"
      :questions="activeQuestions"
      :description="getDescription"
      :primary-color="getThemeHeadingColor"
      :title="formTitle"
      :privacy-policy-url="privacyPolicyUrl"
      :privacy-policy-target="privacyPolicyTarget"
      :invalid-otp-code="invalidOtpCode"
      hide-header
      @submit="handleFormSubmit"
      @send-otp-code="sendCode"
      @resend-otp-code="sendCode"
      @confirm-otp-code="confirmCode"
      @update:answers="setAnswers"
      @update:answers-form-data-format="setAnswersFormData"
    />
    <div
      v-if="submitSuccess"
      class="status"
    >
      <success-check />
      <p class="status-text">
        {{ $t('forms.submitSuccess') }}
      </p>
    </div>
    <div
      v-if="submitError"
      class="status"
    >
      <error-bang />
      <p class="status-text">
        {{ $t('forms.submitError') }}
      </p>
    </div>
    <UnavailableForm
      v-if="unavailableForm"
      :message="unavailableFormMessage"
      hide-btn
    />
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useMainStore } from '../../../stores/mainStore.js';
import { useTranslationStore } from '../../../stores/translationStore.js';
import { useNuxtApp } from '#imports';
import { isEmpty, get } from 'lodash';
import JsonApiResponseConverter from 'json-api-response-converter';
import SuccessCheck from '../../pages/forms/components/svg/SuccessCheck.vue';
import ErrorBang from '../../pages/forms/components/svg/ErrorBang.vue';
import UnavailableForm from '../../pages/forms/views/UnavailableForm.vue';

defineOptions({ name: 'FormBlock' });

const props = defineProps({
  config: {
    type: Object,
    default: () => ({}),
  },
});

const { $axios, $t } = useNuxtApp();
const mainStore = useMainStore();
const translationStore = useTranslationStore();

const activeForm = ref(null);
const activeQuestions = ref('');
const answers = ref([]);
const privacyPolicyUrl = ref('https://www.apptegy.com/privacy-policy/');
const privacyPolicyTarget = ref('_blank');
const invalidOtpCode = ref(false);
const respondentEmail = ref('');
const viewedAt = ref(new Date().toISOString());
const answersFormData = ref(new FormData());
const submitSuccess = ref(false);
const submitError = ref(false);
const unavailableForm = ref(false);

const getLocale = computed(() => translationStore.getLocale);
const getThemeHeadingColor = computed(() => mainStore.getThemeHeadingColor);

const showRenderer = computed(
  () =>
    activeForm.value && activeQuestions.value && !submitSuccess.value && !submitError.value && !unavailableForm.value,
);

const formTitle = computed(() => activeForm.value?.attributes?.name ?? '');

const getDescription = computed(() => activeForm.value?.attributes?.description ?? '');

const unavailableFormMessage = computed(() => $t('forms.unavailableFormTitle', { formTitle: `"${formTitle.value}"` }));

const submitUrl = computed(() => {
  const uniqueId = activeForm.value?.attributes?.unique_id;
  if (!uniqueId) return '';
  return `${useRuntimeConfig().public.FORMS_SERVICE_API_URL}api/v1/forms/${uniqueId}/responses`;
});

const orgParam = computed(() => ({
  secondary_organization_id: mainStore.secondaryOrgId,
}));

function setAnswers(val) {
  answers.value = val;
}

function setAnswersFormData(val) {
  answersFormData.value = val;
}

async function fetchForm() {
  const configFormIndex = get(props.config, 'form.form_id', '-1');

  try {
    const { data } = await $axios.get(
      `${useRuntimeConfig().public.FORMS_SERVICE_API_URL}api/v1/forms/${configFormIndex}`,
      {
        params: {
          locale: getLocale.value !== 'en' ? getLocale.value : undefined,
          status: 'SURVEY_STATUS_ACTIVE',
          ...orgParam.value,
        },
      },
    );
    activeForm.value = data.data;

    if (activeForm.value.attributes.status !== 'SURVEY_STATUS_ACTIVE') {
      throw new Error('Form not currently active');
    }
  } catch {
    unavailableForm.value = true;
  }
}

async function fetchQuestions() {
  try {
    const questionsResponse = await $axios.get(
      `${useRuntimeConfig().public.FORMS_SERVICE_API_URL}api/v1/forms/${activeForm.value.id}/elements`,
      {
        params: {
          locale: getLocale.value !== 'en' ? getLocale.value : undefined,
          include: 'element_choices',
          ...orgParam.value,
        },
      },
    );
    activeQuestions.value = new JsonApiResponseConverter(questionsResponse.data).formattedResponse;
    answers.value = activeQuestions.value.map((question) => ({
      question_id: question.id,
      answer: '',
    }));
  } catch (err) {
    console.error(err);
  }
}

async function handleFormSubmit() {
  try {
    const formData = answersFormData.value;
    formData.append('data[attributes][viewed_at]', viewedAt.value);
    await $axios.post(submitUrl.value, formData, {
      headers: {
        'X-otp-email': respondentEmail.value,
        'Content-Type': 'multipart/form-data',
      },
    });
    submitSuccess.value = true;
  } catch {
    submitError.value = true;
  }
}

async function sendCode(email) {
  respondentEmail.value = respondentEmail.value || email;
  await $axios.post(`${useRuntimeConfig().public.FORMS_SERVICE_API_URL}api/v1/email_verification`, {
    data: {
      attributes: {
        email: respondentEmail.value,
        form_unique_id: activeForm.value?.attributes?.unique_id,
      },
    },
  });
}

async function confirmCode(code) {
  invalidOtpCode.value = false;
  const {
    data: { success },
  } = await $axios.post(`${useRuntimeConfig().public.FORMS_SERVICE_API_URL}api/v1/otp_verification`, {
    data: {
      attributes: {
        email: respondentEmail.value,
        otp: code,
      },
    },
  });
  if (!success) {
    invalidOtpCode.value = true;
  } else {
    await handleFormSubmit();
  }
}

onMounted(async () => {
  if (!isEmpty(props.config)) {
    await fetchForm();

    if (unavailableForm.value) return;

    await fetchQuestions();
  }
});
</script>

<style scoped lang="scss">
.status {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}

.status-text {
  display: inline-block;
  font-size: 24px;
  line-height: 32px;
  font-weight: 900;
  letter-spacing: 0.8px;
}
</style>
